import React from 'react';
import ReactDOM from 'react-dom';
import T from '../styles/theme';
import TK from '../styles/tokens';
import Icon from './Icon';

export function fmtDollar(val) {
  if (val == null || isNaN(val)) return "$0";
  const abs = Math.abs(val);
  const sign = val < 0 ? "-" : "";
  if (abs >= 1000000) return sign + "$" + (abs / 1000000).toFixed(abs % 1000000 === 0 ? 0 : 1).replace(/\.0$/, "") + "M";
  if (abs >= 1000) return sign + "$" + (abs / 1000).toFixed(abs % 1000 === 0 ? 0 : 0) + "K";
  return sign + "$" + abs.toFixed(0);
}

export const glass = () => ({ ...TK.card });

export function Badge({ children }) {
  return (<span style={TK.badge}>{children}</span>);
}

export function Avatar({ text, size, url }) {
  const t = text || "??";
  const sz = size || 34;
  if (url) {
    return (
      <div style={{ width: sz, height: sz, borderRadius: TK.RADIUS, overflow: "hidden", flexShrink: 0, border: "1px solid " + TK.BORDER }}>
        <img src={url} alt={t} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
      </div>
    );
  }
  return (
    <div style={{ width: sz, height: sz, borderRadius: TK.RADIUS, display: "flex", alignItems: "center", justifyContent: "center", background: TK.BG_SUBTLE, color: TK.TEXT, fontSize: sz * 0.33, fontWeight: 400, flexShrink: 0, border: "1px solid " + TK.BORDER }}>{t}</div>
  );
}

export function ProgressBar({ value, max, color, height }) {
  const mx = max || 100; const c = color || TK.BLUE; const h = height || 4;
  return (
    <div style={{ width: "100%", height: h, background: "rgba(255,255,255,0.08)", borderRadius: h / 2, overflow: "hidden" }}>
      <div style={{ width: Math.min((value / mx) * 100, 100) + "%", height: "100%", background: c, borderRadius: h / 2, transition: "width 0.6s cubic-bezier(0.16, 1, 0.3, 1)" }} />
    </div>
  );
}

export function KPICard({ label, value, change, up, prefix, suffix }) {
  let displayVal;
  if (prefix === "$" && typeof value === "number") { displayVal = fmtDollar(value); prefix = ""; }
  else { displayVal = (prefix || "") + (typeof value === "number" ? value.toLocaleString() : value) + (suffix || ""); }
  return (
    <div style={{ ...TK.card, padding: "16px 18px", flex: 1, minWidth: 150 }}>
      <div style={TK.label}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginTop: 6 }}>
        <span style={{ fontSize: 22, fontWeight: 400, color: TK.TEXT }}>{displayVal}{suffix || ""}</span>
        {change && <span style={{ fontSize: 10, fontWeight: 400, color: TK.TEXT_DIM }}>{up ? "+" : "-"}{change}</span>}
      </div>
    </div>
  );
}

export function Btn({ children, primary, onClick, disabled, style: sx }) {
  return (
    <button onClick={onClick} disabled={disabled}

      style={{ ...(primary ? TK.btnPrimary : TK.btn), opacity: disabled ? 0.5 : 1, ...sx }}>
      {children}
    </button>
  );
}

export function Card({ children, style: sx }) {
  return (<div style={{ ...TK.card, ...sx }}>{children}</div>);
}

export function GlassInput({ value, onChange, placeholder, style: sx, ...rest }) {
  return (
    <input value={value} onChange={onChange} placeholder={placeholder} {...rest}
      onFocus={TK.inputFocusOn} onBlur={TK.inputFocusOff}
      style={{ ...TK.input, width: "100%", ...sx }} />
  );
}

export function Checkbox({ checked, onChange, size }) {
  const sz = size || 20;
  // Square at small sizes (≤16) to keep the rounded-square silhouette;
  // 8px at default 20+ matches the existing ContactsList/DealsPage look.
  // Icon scales with sz so a 14px checkbox doesn't overflow its border.
  const radius = sz <= 16 ? 4 : 8;
  const iconSz = Math.max(8, Math.round(sz * 0.6));
  return (
    <button type="button" onClick={onChange} style={{
      width: sz, height: sz, borderRadius: radius, cursor: "pointer",
      border: checked ? "1.5px solid #4059AD" : "1.5px solid rgba(217,219,221,0.12)",
      background: checked ? "#4059AD" : "transparent",
      display: "flex", alignItems: "center", justifyContent: "center",
      flexShrink: 0, transition: "all 0.15s", padding: 0,
    }}>
      {checked && <Icon name="check" size={iconSz} color="#FAFAFA" />}
    </button>
  );
}

export function Select({ value, onChange, children, options, placeholder, style: sx, ...rest }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const dropRef = React.useRef(null);

  let items = [];
  if (options) { items = options.map(o => typeof o === "string" ? { value: o, label: o } : o); }
  else if (children) { React.Children.forEach(children, child => { if (child?.props) items.push({ value: child.props.value ?? "", label: child.props.children ?? child.props.value ?? "" }); }); }

  const selected = items.find(i => i.value === value);
  const displayLabel = selected?.label || placeholder || "— Select —";

  React.useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current?.contains(e.target)) return; if (dropRef.current?.contains(e.target)) return; setOpen(false); };
    setTimeout(() => document.addEventListener("click", close), 0);
    return () => document.removeEventListener("click", close);
  }, [open]);

  return (
    <div ref={ref} style={{ position: "relative", width: "100%", ...sx, border: "none", background: "none", padding: 0 }} {...rest}>
      <button type="button" onClick={() => setOpen(!open)}

        style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", ...TK.input, cursor: "pointer", borderColor: open ? TK.BLUE : TK.BORDER, boxShadow: "none" }}>
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>{displayLabel}</span>
        <svg width="10" height="6" viewBox="0 0 10 6" style={{ flexShrink: 0, marginLeft: 8, transform: open ? "rotate(180deg)" : "none", transition: "transform 0.15s" }}>
          <path d="M1 1l4 4 4-4" stroke={TK.TEXT_MUTED} strokeWidth="1.5" fill="none" strokeLinecap="round"/>
        </svg>
      </button>
      {open && ReactDOM.createPortal(
        <>
        <div style={{ position: "fixed", inset: 0, zIndex: 99998 }} onClick={() => setOpen(false)} />
        <div ref={dropRef} style={{ position: "fixed", top: (ref.current?.getBoundingClientRect()?.bottom || 0) + 4, left: ref.current?.getBoundingClientRect()?.left || 0, width: ref.current?.getBoundingClientRect()?.width || 200, zIndex: 99999, ...TK.dropdown, maxHeight: 240, overflowY: "auto", padding: "4px" }}>
          {items.map((item, i) => (
            <button key={item.value + "-" + i} type="button" onClick={() => { if (onChange) onChange({ target: { value: item.value } }); setOpen(false); }}
              style={{ width: "100%", padding: "9px 12px", borderRadius: TK.RADIUS, border: "none", cursor: "pointer", background: item.value === value ? TK.BLUE : "transparent", color: TK.TEXT, fontSize: 12, fontWeight: 400, fontFamily: "inherit", textAlign: "left", display: "block", transition: "background 0.12s" }}
              onMouseEnter={e => { if (item.value !== value) e.target.style.background = TK.BG_SUBTLE; }}
              onMouseLeave={e => { if (item.value !== value) e.target.style.background = "transparent"; }}>
              {item.label || item.value || "—"}
            </button>
          ))}
        </div>
        </>, document.body
      )}
    </div>
  );
}
